// src/components/Modals/ConfirmLogoutModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFeedback } from "../../contexts/FeedbackContext";

const ConfirmLogoutModal = ({ onConfirm, onCancel, open = true }) => {
  const { play, buzz } = useFeedback?.() || { play: () => {}, buzz: () => {} };
  const dialogRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const first = dialogRef.current?.querySelector?.("[data-autofocus]");
    first?.focus?.();

    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        play("tap"); buzz("light");
        onCancel?.();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        handleConfirm();
      }
      if (e.key.toLowerCase() === "y" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        handleConfirm();
      }
      if (e.key.toLowerCase() === "n" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        onCancel?.();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function handleConfirm() {
    if (busy) return;
    setBusy(true);
    play("stop"); buzz("light");
    Promise.resolve(onConfirm?.()).finally(() => setBusy(false));
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 font-sans"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-title"
      onMouseDown={(e) => {
        // backdrop click → cancel (but ignore clicks inside panel)
        if (e.target === e.currentTarget) {
          play("tap"); buzz("light");
          onCancel?.();
        }
      }}
    >
      <AnimatePresence initial={false}>
        <motion.div
          ref={dialogRef}
          initial={{ opacity: 0, scale: 0.95, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 6 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white dark:bg-[#1a1a1a] text-black dark:text-white p-6 rounded-xl shadow-2xl max-w-md w-full border border-black/10 dark:border-white/10"
        >
          <h2 id="logout-title" className="text-xl font-semibold mb-2">
            Log out?
          </h2>
          <p className="text-sm opacity-80 mb-5">
            You’ll need to sign in again to access your studio data.
          </p>

          <div className="flex justify-between items-center gap-3">
            <div className="text-xs opacity-60">Esc to cancel • ⌘/Ctrl+Enter to confirm</div>
            <div className="flex gap-3">
              <button
                onClick={() => { play("tap"); buzz("light"); onCancel?.(); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                data-autofocus
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={busy}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded disabled:opacity-60"
              >
                {busy ? "Logging out…" : "Log Out"}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ConfirmLogoutModal;
